<template>
    <section class="favorite-books-page" aria-labelledby="favorite-books-title" :aria-busy="loading ? 'true' : 'false'">
        <header class="mb-5">
            <h1 id="favorite-books-title" ref="heading" tabindex="-1" class="text-2xl font-extrabold tracking-tight text-foxy-text">Улюблене</h1>
            <p class="mt-1 text-sm text-foxy-text-secondary">Книги<span v-if="loaded"> · {{ pagination.total }}</span></p>
        </header>

        <p v-if="loading" class="py-8 text-sm text-foxy-text-secondary" role="status">Завантажуємо улюблені книги…</p>
        <div v-else-if="error" class="rounded-2xl border border-foxy-error/40 bg-foxy-error/10 p-5 text-sm text-foxy-text" role="alert">
            <p>{{ error }}</p>
            <button type="button" class="acc-btn mt-3" @click="loadPage">Спробувати ще раз</button>
        </div>
        <template v-else-if="loaded">
            <div v-if="!items.length" class="library-shelves-message">
                <p class="font-bold text-foxy-text">{{ isOwner ? 'У вас ще немає улюблених книг.' : 'У цьому профілі ще немає улюблених книг.' }}</p>
                <p v-if="isOwner" class="mt-2 text-sm text-foxy-text-secondary">Додати книгу до улюбленого можна на її сторінці.</p>
            </div>
            <div v-else class="favorite-books-grid">
                <article v-for="item in items" :key="item.work_id" class="book-shelf-item block min-w-0 rounded-xl" data-favorite-book>
                    <a :href="item.url" :aria-label="'Відкрити книгу «' + item.title + '»'"
                        class="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-bg"
                        @mouseenter="openPreview(item, $event)" @mouseleave="schedulePreviewClose"
                        @focus="openPreview(item, $event, true)" @blur="schedulePreviewClose">
                        <div class="book-cover-frame relative aspect-[2/3] rounded-xl shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                            <img v-if="item.cover" :src="item.cover" class="book-cover-media" alt="" loading="lazy" decoding="async" width="200" height="300" />
                            <span v-else class="favorite-book-cover-fallback" aria-hidden="true"><i class="fas fa-book"></i></span>
                            <book-status-bookmark v-if="viewerStatus(item)" :status="viewerStatus(item)" />
                        </div>
                    </a>
                    <div class="mt-2.5 px-0.5">
                        <a :href="item.url" class="group block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action" :title="item.title">
                            <h2 class="text-sm font-semibold text-foxy-text leading-snug line-clamp-2 group-hover:text-foxy-action transition-colors">{{ item.title }}</h2>
                        </a>
                        <p class="favorite-book-author mt-1 text-xs text-foxy-text-secondary line-clamp-1">
                            <template v-if="item.authors && item.authors.length">
                                <span v-for="(author, index) in item.authors" :key="index">
                                    <span v-if="index">, </span><a v-if="author.url" :href="author.url"
                                        class="book-author-link rounded-sm transition-colors hover:text-foxy-action focus-visible:text-foxy-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action">{{ author.name }}</a><span v-else>{{ author.name }}</span>
                                </span>
                            </template>
                            <template v-else>{{ item.author && item.author.name }}</template>
                        </p>
                    </div>
                    <button v-if="previewAvailable" type="button" class="book-preview-trigger acc-btn hidden text-xs md:inline-flex"
                        :aria-label="'Швидкий перегляд книги «' + item.title + '»'"
                        :aria-controls="previewId" :aria-expanded="previewOpenWorkId === item.work_id ? 'true' : 'false'"
                        :disabled="!previewItem(item)" @focus="openPreview(item, $event, true)" @blur="schedulePreviewClose"
                        @click="openPreview(item, $event, true)" @keydown="previewKeydown(item, $event)">
                        Швидкий перегляд
                    </button>
                </article>
            </div>
            <div v-if="contextState === 'error'" class="mt-4 text-sm text-foxy-text-secondary" role="status">
                <p>Не вдалося завантажити дані вашої бібліотеки.</p>
                <button type="button" class="acc-btn mt-2" @click="refreshViewerContexts()">Спробувати ще раз</button>
            </div>
            <nav v-if="pagination.last_page > 1" class="mt-6 flex items-center justify-center gap-3" aria-label="Сторінки улюблених книг">
                <button type="button" class="acc-btn" :disabled="loading || pagination.current_page <= 1" @click="goPage(pagination.current_page - 1)">Назад</button>
                <span class="text-sm text-foxy-text-secondary" aria-live="polite">{{ pagination.current_page }} / {{ pagination.last_page }}</span>
                <button type="button" class="acc-btn" :disabled="loading || pagination.current_page >= pagination.last_page" @click="goPage(pagination.current_page + 1)">Далі</button>
            </nav>
        </template>
        <book-quick-preview-host v-if="previewAvailable" ref="quickPreview" :items="previewItems"
            :refresh-items="refreshViewerContexts" surface-context="PUBLIC_FOREIGN_LIBRARY" :authenticated="previewAvailable"
            @manage="openViewerManagement" @opened="previewOpenWorkId = $event" @closed="previewOpenWorkId = null" />
    </section>
</template>

<script>
import axios from 'axios';
import BookQuickPreviewHost from '../../components/books/BookQuickPreviewHost.vue';
import BookStatusBookmark from '../../components/books/BookStatusBookmark.vue';
import { canUseBookQuickPreview } from '../../utils/bookQuickPreviewCapability';
import { isVisibleBookStatus } from '../../utils/bookStatusPresentation';

export default {
    name: 'ProfileFavorites',
    components: { BookQuickPreviewHost, BookStatusBookmark },
    props: {
        profile: { type: Object, required: true },
        viewer: { type: Object, default: null },
        isOwner: { type: Boolean, default: false },
        isRestricted: { type: Boolean, default: false },
    },
    data() {
        return {
            items: [], pagination: { current_page: 1, last_page: 1, per_page: 24, total: 0 },
            loading: false, loaded: false, error: '', requestGeneration: 0, contextGeneration: 0,
            viewerContexts: {}, bookFacts: {}, contextState: 'idle', previewOpenWorkId: null,
            previewId: 'book-quick-preview-owner-library',
        };
    },
    computed: {
        previewAvailable() { return canUseBookQuickPreview(Boolean(this.viewer)); },
        previewItems() { return this.items.map(this.previewItem).filter(Boolean); },
    },
    watch: {
        '$route.fullPath': { immediate: true, handler() { return this.loadPage(); } },
        'viewer.id'() {
            this.$refs.quickPreview?.close({ restoreFocus: false });
            this.viewerContexts = {};
            this.bookFacts = {};
            this.refreshViewerContexts();
        },
    },
    beforeDestroy() {
        this.requestGeneration += 1;
        this.contextGeneration += 1;
        this.$refs.quickPreview?.close({ restoreFocus: false });
    },
    methods: {
        normalizedQuery() {
            const query = { ...this.$route.query };
            delete query.type;
            const raw = query.page;
            if (raw !== undefined && (typeof raw !== 'string' || !/^[1-9]\d*$/.test(raw) || !Number.isSafeInteger(Number(raw)))) delete query.page;
            return query;
        },
        replaceQuery(query) {
            return this.$router.replace({ name: 'acc.profile.favorites', params: this.$route.params, query });
        },
        async loadPage() {
            const generation = ++this.requestGeneration;
            this.contextGeneration += 1;
            this.$refs.quickPreview?.close({ restoreFocus: false });
            this.viewerContexts = {};
            this.bookFacts = {};
            this.contextState = 'idle';
            if (this.isRestricted && !this.isOwner) {
                this.items = [];
                this.loaded = false;
                return this.$router.replace({ name: 'acc.profile.overview', params: { username: this.profile.username } });
            }
            const query = this.normalizedQuery();
            if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) return this.replaceQuery(query);
            const page = Number(query.page || 1);
            this.loading = true;
            this.loaded = false;
            this.error = '';
            this.items = [];
            try {
                const { data } = await axios.get('/api/account/profile/' + encodeURIComponent(this.profile.username) + '/favorites', {
                    params: { type: 'books', page },
                });
                if (generation !== this.requestGeneration) return;
                if (!Array.isArray(data?.items) || data.items.length > 24 || !data.pagination || data.type !== 'books') throw new Error('Invalid Favorite Books response');
                if (page > data.pagination.last_page) {
                    const recovered = { ...query, page: String(Math.max(1, data.pagination.last_page)) };
                    return this.replaceQuery(recovered);
                }
                this.items = data.items;
                this.pagination = data.pagination;
                this.loaded = true;
                await this.refreshViewerContexts();
            } catch (error) {
                if (generation !== this.requestGeneration) return;
                this.items = [];
                this.loaded = false;
                if (error?.response?.status === 403) {
                    await this.$router.replace({ name: 'acc.profile.overview', params: { username: this.profile.username } });
                    return;
                }
                this.error = error?.response?.data?.message || 'Не вдалося завантажити улюблені книги.';
            } finally {
                if (generation === this.requestGeneration) {
                    this.loading = false;
                    if (this.loaded && query.page !== undefined) this.$nextTick(() => this.$refs.heading?.focus({ preventScroll: true }));
                }
            }
        },
        async refreshViewerContexts() {
            const generation = ++this.contextGeneration;
            const pageGeneration = this.requestGeneration;
            const viewerId = this.viewer?.id;
            const username = this.profile.username;
            const ids = this.items.map((item) => item.work_id);
            const current = () => generation === this.contextGeneration && pageGeneration === this.requestGeneration &&
                viewerId === this.viewer?.id && username === this.profile.username;
            if (!viewerId || !ids.length) {
                this.viewerContexts = {};
                this.bookFacts = {};
                this.contextState = 'loaded';
                return;
            }
            this.contextState = 'loading';
            try {
                const { data } = await axios.get('/api/account/book-preview-contexts', { params: { book_ids: ids } });
                if (!current()) return;
                if (Number(data?.viewer_id) !== Number(viewerId) || !data.viewer_contexts ||
                    ids.some((id) => !Object.prototype.hasOwnProperty.call(data.viewer_contexts, id))) throw new Error('Invalid viewer snapshot');
                this.viewerContexts = data.viewer_contexts;
                this.bookFacts = data.book_facts || {};
                this.contextState = 'loaded';
            } catch {
                if (!current()) return;
                this.viewerContexts = {};
                this.bookFacts = {};
                this.contextState = 'error';
                this.$refs.quickPreview?.close({ restoreFocus: false });
            }
        },
        viewerStatus(item) {
            const status = this.viewerContexts[item.work_id]?.shelf_status;
            return this.viewer && this.contextState === 'loaded' && isVisibleBookStatus(status) ? status : null;
        },
        previewItem(item) {
            const context = this.viewerContexts[item.work_id];
            if (!this.previewAvailable || this.contextState !== 'loaded' || !context) return null;
            // Membership stays in the profile archive; only viewer-owned context enters Preview.
            return {
                id: item.work_id,
                book: { id: item.work_id, slug: item.slug, title: item.title, cover: item.cover, ...this.bookFacts[item.work_id] },
                status: context.shelf_status, card_context: context,
            };
        },
        openPreview(item, event, immediate = false) {
            const preview = this.previewItem(item);
            if (preview) this.$refs.quickPreview?.scheduleOpen(preview, event.currentTarget, immediate);
        },
        schedulePreviewClose() { this.$refs.quickPreview?.scheduleClose(); },
        previewKeydown(item, event) {
            const preview = this.previewItem(item);
            if (preview) this.$refs.quickPreview?.handleTriggerKeydown(event, preview, event.currentTarget);
        },
        openViewerManagement() {
            const url = this.$refs.quickPreview?.activeContext?.managementUrl;
            if (url) window.location.assign(url);
        },
        async goPage(page) {
            if (this.loading || page < 1 || page > this.pagination.last_page) return;
            await this.$router.push({ name: 'acc.profile.favorites', params: this.$route.params, query: { ...this.normalizedQuery(), page: String(page) } });
            window.scrollTo({ top: 0, behavior: 'auto' });
        },
    },
};
</script>

<style scoped>
.favorite-books-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 1rem; row-gap: 2rem; }
.favorite-book-cover-fallback { display: flex; height: 100%; align-items: center; justify-content: center; color: rgb(var(--foxy-text-secondary)); font-size: 2rem; }
/* Match the existing Library keyboard-only Preview affordance. */
.book-preview-trigger {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0 !important;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}
.book-preview-trigger:focus-visible {
    position: static;
    width: auto;
    height: 2.5rem;
    margin: 0;
    padding: 0.5rem 0.75rem !important;
    overflow: visible;
    clip: auto;
    white-space: normal;
    border-width: 1px;
}
.favorite-books-page h1:focus-visible { outline: 2px solid rgb(var(--foxy-action)); outline-offset: 3px; }
@media (min-width: 640px) { .favorite-books-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); column-gap: 1.25rem; row-gap: 2.5rem; } }
@media (min-width: 768px) { .favorite-books-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
@media (min-width: 1024px) { .favorite-books-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
@media (min-width: 1280px) { .favorite-books-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); } }
</style>
